import type { ReactNode } from 'react';
import { Link } from 'react-router';
import PicturizedImage from '@/components/common/picturized-image';
import { emoticons } from '@/components/symbol/emoticon/emoticons';
import ROUTES from '@/routes';
import { escapeRegExp } from '@/utils/escape-regexp';

const emojiRegexp = RegExp(/\[e\d+]/, 'ig');
const linkRegexp = RegExp(/\b[\w+.-]+:\/\/[^\s<>"']+/, 'gi');
const mentionRegexp = /(?:^|\s)@/g;
const allPeopleRegexp = /(?:^|\s)(?:@所有人|@All)/gi;
const breakRegexp = RegExp(/\r\n|\n|\r/);

export enum ScriptType {
  LineBreak = 0,
  Emoticon = 1,
  Link = 2,
  Mention = 3,
}

type ScriptNodes = Array<[ScriptType, string] | string>;

export function transformSpecialScripts(
  id: number,
  message: string,
  members: {
    id: number;
    groupId: number | undefined;
    groupName: Nullable<string>;
    userName: string;
  }[] = [],
  transforms: ScriptType[] = [ScriptType.LineBreak, ScriptType.Link, ScriptType.Emoticon, ScriptType.Mention],
): ReactNode {
  let instructions: ScriptNodes = [message];

  if (transforms.includes(ScriptType.LineBreak)) {
    instructions = instructions.reduce<ScriptNodes>((nodes, node) => {
      if (typeof node === 'string' && breakRegexp.test(node)) {
        node.split(breakRegexp).forEach((line, index, array) => {
          if (line !== '') {
            nodes.push(line);
            if (index < array.length - 1) {
              nodes.push([ScriptType.LineBreak, '']);
            }
          }
        });
      } else {
        nodes.push(node);
      }

      return nodes;
    }, []);
  }

  if (transforms.includes(ScriptType.Emoticon)) {
    instructions = instructions.reduce<ScriptNodes>((nodes, node) => {
      if (typeof node !== 'string') {
        nodes.push(node);
      } else if (node === '') {
        // NOTE: do nothing
      } else {
        if (emojiRegexp.test(node)) {
          const matches = node.match(emojiRegexp);

          if (matches) {
            node.split(emojiRegexp).forEach((part, index, array) => {
              nodes.push(part);
              const matched = matches[index];
              if (index < array.length - 1 && matched) {
                if (matched in emoticons) {
                  nodes.push([ScriptType.Emoticon, matched]);
                } else {
                  nodes.push(matched);
                }
              }
            });
          }
        } else {
          nodes.push(node);
        }
      }

      return nodes;
    }, []);
  }

  if (transforms.includes(ScriptType.Link)) {
    instructions = instructions.reduce<ScriptNodes>((nodes, node) => {
      if (typeof node !== 'string' || node === '') {
        nodes.push(node);
        return nodes;
      }

      let lastIndex = 0;
      for (const match of node.matchAll(linkRegexp)) {
        const { index } = match;
        if (index === undefined) continue;

        if (index > lastIndex) nodes.push(node.slice(lastIndex, index));
        nodes.push([ScriptType.Link, match[0]]);
        lastIndex = index + match[0].length;
      }

      if (lastIndex < node.length) nodes.push(node.slice(lastIndex));
      return nodes;
    }, []);
  }

  if (transforms.includes(ScriptType.Mention) && Array.isArray(members)) {
    instructions = instructions.reduce<ScriptNodes>((nodes, node) => {
      if (typeof node !== 'string') {
        nodes.push(node);
      } else if (node === '') {
        // NOTE: do nothing
      } else {
        if (mentionRegexp.test(node)) {
          const matches = node.match(mentionRegexp);
          if (matches) {
            const remain = matches.reduce((content, match) => {
              const index = content.indexOf('@');
              const preMatch = content.slice(0, index);

              const allPeopleMention = content.match(allPeopleRegexp)?.[0];
              if (allPeopleMention && content.indexOf(allPeopleMention) === index) {
                if (preMatch) nodes.push(preMatch);
                nodes.push([ScriptType.Mention, allPeopleMention]);
                return content.slice(index + allPeopleMention.length);
              }

              const exist = content.match(
                RegExp(
                  members
                    .map(
                      ({ groupName, userName }) =>
                        `@${escapeRegExp(userName)}${groupName ? `|@${escapeRegExp(groupName)}` : ''}`,
                    )
                    .join('|'),
                ),
              )?.[0];
              if (exist) {
                if (preMatch) nodes.push(preMatch);
                nodes.push([ScriptType.Mention, exist]);
                return content.slice(index + exist.length);
              }
              nodes.push(match);
              return content.slice(index + match.length);
            }, node);

            if (remain) {
              nodes.push(remain);
            }
          }
        } else {
          nodes.push(node);
        }
      }

      return nodes;
    }, []);
  }

  return instructions
    .map<ReactNode>((node, index) => {
      if (typeof node === 'string') {
        return node;
      }
      if (node[0] === ScriptType.LineBreak) {
        return <br key={`${id}-LineBreak-${index}`} />;
      }
      if (node[0] === ScriptType.Emoticon) {
        const imageData = emoticons[node[1]];
        return imageData ? (
          <PicturizedImage
            key={`${id}-Emoji-${index}`}
            className='inline-block data-quote:size-5 size-9'
            picture={imageData}
            alt={node[1]}
          />
        ) : null;
      }
      if (node[0] === ScriptType.Link) {
        return (
          <a
            key={`${id}-Link-${index}`}
            className='link break-all'
            href={node[1]}
            target='_blank'
            rel='noreferrer'>
            {node[1]}
          </a>
        );
      }
      if (node[0] === ScriptType.Mention) {
        const mention = members.find(
          ({ groupName, userName }) => groupName === node[1].slice(1) || userName === node[1].slice(1),
        );

        if (node[1].match(allPeopleRegexp)) {
          return (
            <span
              key={`${id}-Mention-${index}`}
              className='text-blue-600'>
              {node[1]}
            </span>
          );
        }

        return mention ? (
          <Link
            to={ROUTES.CONTACT.USERS.BY_ID.$buildPath({
              params: { userId: mention.id },
              searchParams: { groupId: mention.groupId },
            })}
            key={`${id}-Mention-${index}`}
            className='text-blue-600'>
            {node[1]}
          </Link>
        ) : (
          node[1]
        );
      }

      return node;
    })
    .filter((node) => Boolean(node));
}
